import React, { useState, FormEvent } from "react";
import emailjs from "emailjs-com";
import Header from "../Header";
import {
  Box,
  styled,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { ContactItem } from "./ContactItem";
import { ProjectTypeSelector } from "./ProjectTypeSelector";
import { AgreementCheckbox } from "./AgreementCheckbox";
import { ContactSubmitButton } from "./ContactSubmitButton";
import { getStaticFileUrl } from "../utils/config";


// ✅ 스타일 유지
const ContentsMain = styled("main")(() => ({
  position: "relative",
  backgroundColor: "#0a0e2d",
}));

const WrapDiv = styled("div")(() => ({
  width: "90%",
  maxWidth: 1200,
  margin: "0 auto",
}));

const ContactWrap = styled("div")(() => ({
  position: "relative",
  zIndex: 1,
  marginTop: "80px",
  "@media (max-width: 1000px)": {
    padding: "0 24px",
  },
}));

const ContactText = styled("p")(() => ({
  position: "relative",
  marginBottom: "69px",
  paddingBottom: "14px",
  borderBottom: "1px solid #14f1a5",
  color: "#14f1a5",
  fontSize: "22px",
  fontWeight: 600,
  lineHeight: "33px",
  letterSpacing: "-1px",
  "& span": {
    float: "right",
    position: "relative",
    color: "#fff",
    fontSize: "20px",
    fontWeight: 500,
    "&:before": {
      content: '"* "',
      color: "#14f1a5",
      marginRight: "4px",
    },
  },
  "@media (max-width: 1000px)": {
    marginBottom: "47px",
    paddingBottom: "9px",
    fontSize: "18px",
    textAlign: "center",
    lineHeight: "27px",
    "& span": {
      float: "none",
      position: "absolute",
      right: 0,
      bottom: "-28px",
      fontSize: "12px",
      lineHeight: "18px",
    },
  },
}));

interface FormValues {
  projectTypes: string[];
  projectContent: string;
  db_company: string;
  db_name: string;
  db_email: string;
  db_tel1: string;
  db_tel2: string;
  db_tel3: string;
  agreePrivacy: boolean;
}

const initialFormValues: FormValues = {
  projectTypes: [],
  projectContent: "",
  db_company: "",
  db_name: "",
  db_email: "",
  db_tel1: "",
  db_tel2: "",
  db_tel3: "",
  agreePrivacy: false,
};

// EmailJS 본인 정보로 바꿔주세요
const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID as string;
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID as string;
const EMAILJS_PUBLIC_KEY  = process.env.REACT_APP_EMAILJS_PUBLIC_KEY as string;

export const Contact: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [formValues, setFormValues] = useState<FormValues>(initialFormValues);

  const handleChangeText =
    (key: keyof FormValues) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFormValues((prev) => ({ ...prev, [key]: e.target.value }));
    };

  const handleAgreePrivacy = () => {
    setFormValues((prev) => ({ ...prev, agreePrivacy: !prev.agreePrivacy }));
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!formValues.projectTypes.length) {
      alert("[필수] 프로젝트 유형을 하나 이상 선택해주세요.");
      return;
    }
    if (!formValues.projectContent.trim()) {
      alert("[필수] 문의 내용을 입력해주세요.");
      return;
    }
    if (!formValues.agreePrivacy) {
      alert("[필수] 개인정보 수집/이용에 동의해 주세요.");
      return;
    }

    // EmailJS로 보낼 변수 (`templateParams`의 key는 EmailJS 템플릿의 변수명과 반드시 같아야 함!)
    const templateParams = {
      project_types: formValues.projectTypes.join(", "),
      project_content: formValues.projectContent,
      db_company: formValues.db_company,
      db_name: formValues.db_name,
      db_email: formValues.db_email,
      db_tel: [formValues.db_tel1, formValues.db_tel2, formValues.db_tel3].join(
        "-"
      ),
    };

    try {
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        templateParams,
        EMAILJS_PUBLIC_KEY
      );
      alert("프로젝트 문의가 등록되고, 메일이 발송되었습니다!");
      setFormValues(initialFormValues);
    } catch (err) {
      alert("메일 발송에 실패했습니다. 다시 시도해주세요.");
      console.error(err);
    }
  };

  return (
    <Box sx={{ overflowX: "hidden" }}>
      <Header />
      <Box
        sx={{
          width: "100vw",
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#0a0e2d",
        }}
      >
        <Box
          component="header"
          sx={{
            width: "100%",
            height: "55vh",
            textAlign: "center",
            alignItems: "center",
            justifyContent: "center",
            color: "rgb(0, 0, 0)",
            paddingTop: "120px",
            marginBottom: "30px",
            overflow: "hidden",
            backgroundSize: "cover",
            backgroundPosition: "center top",
            backgroundRepeat: "no-repeat",
            backgroundImage: `
              linear-gradient(
                rgba(0, 0, 0, 0.75), 
                rgba(0, 0, 0, 0.75)
              ), 
              url(${getStaticFileUrl('/img/contact/banner.png')})
            `,
          }}
        >
          <Box
            component="img"
            src={getStaticFileUrl("/img/logo.png")}
            alt="Logo"
            sx={{
              marginTop: "4rem",
              width: "230px",
              height: "auto",
              marginBottom: "1.5rem",
              "@media (max-width: 960px)": {
                width: "140px",
              },
              "@media (max-width: 600px)": {
                width: "140px",
              },
              animation: "slideUp 0.5s ease-out forwards",
              animationDelay: "0.1s",
              "@keyframes slideUp": {
                from: {
                  transform: "translateY(100%)",
                },
                to: {
                  transform: "translateY(0)",
                },
              },
            }}
          />
          <Box
            sx={{
              width: "1px",
              height: "2.5rem",
              background: "#fff",
              margin: "0 auto 15px",
              borderRadius: "2px",
              animation: "slideUp 0.5s ease-out forwards",
              animationDelay: "0.1s",
              "@keyframes slideUp": {
                from: {
                  transform: "translateY(100%)",
                },
                to: {
                  transform: "translateY(0)",
                },
              },
            }}
          />
          <Box sx={{ overflow: "hidden" }}>
            <Typography
              variant="h5"
              sx={{
                fontSize: isMobile ? "1.1rem" : "1.5rem",
                padding: "10px 0",
                color: "#fff",
                fontWeight: "300",
                animation: "slideUp 0.5s ease-out forwards",
                animationDelay: "0.1s",
                "@keyframes slideUp": {
                  from: {
                    transform: "translateY(100%)",
                  },
                  to: {
                    transform: "translateY(0)",
                  },
                },
              }}
            >
              CONTACT
              <br />
              CONTENTS & COMMUNICATION
            </Typography>
          </Box>
        </Box>
        <ContentsMain>
          <WrapDiv>
            <ContactWrap>
              <form onSubmit={handleSubmit} autoComplete="off">
                <ContactText>
                  진행하고자 하는 프로젝트에 대해 알려주세요.
                  <span>중복 선택 가능</span>
                </ContactText>

                <ProjectTypeSelector
                  selectedTypes={formValues.projectTypes}
                  setSelectedTypes={(types) =>
                    setFormValues((prev) => ({ ...prev, projectTypes: types }))
                  }
                />

                <ContactItem
                  label="[필수] 문의 내용을 남겨주세요."
                  value={formValues.projectContent}
                  onChange={handleChangeText("projectContent")}
                  placeholder="프로젝트에 대한 상세 설명을 입력해주세요."
                  isTextarea
                />
                <Box sx={{ padding: "3rem 0" }}></Box>
                <ContactText>
                  의뢰하시는 담당자 정보를 입력해 주세요.
                </ContactText>

                <ContactItem
                  label="[필수] 회사명"
                  value={formValues.db_company}
                  onChange={handleChangeText("db_company")}
                  placeholder="회사명을 입력해주세요."
                />

                <ContactItem
                  label="[필수] 이름"
                  value={formValues.db_name}
                  onChange={handleChangeText("db_name")}
                  placeholder="이름을 입력해주세요."
                />

                <ContactItem
                  label="[필수] 이메일"
                  value={formValues.db_email}
                  onChange={handleChangeText("db_email")}
                  placeholder="이메일을 입력해주세요."
                />

                <ContactItem
                  label="[필수] 연락처"
                  isPhone
                  phoneValues={{
                    tel1: formValues.db_tel1,
                    tel2: formValues.db_tel2,
                    tel3: formValues.db_tel3,
                  }}
                  onPhoneChange={(key, value) =>
                    setFormValues((prev) => ({
                      ...prev,
                      [`db_${key}`]: value,
                    }))
                  }
                />

                <AgreementCheckbox
                  checked={formValues.agreePrivacy}
                  onChange={handleAgreePrivacy}
                />

                <ContactSubmitButton />
              </form>
            </ContactWrap>
          </WrapDiv>
        </ContentsMain>
      </Box>
    </Box>
  );
};